<template>
  <div
    class="modal fade"
    id="item-batch-details-modal"
    tabindex="-1"
    aria-labelledby="itemBatchDetailsModalLabel"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
      <div class="modal-content">
        <div class="modal-header border-0">
          <div>
            <p class="batch-eyebrow mb-1">{{ $t('batch_details') }}</p>
            <h4 id="itemBatchDetailsModalLabel" class="mb-0">
              {{ item.item_name || '—' }}
            </h4>
            <p class="text-muted small mb-0 mt-1">
              <span v-if="item.item_code">{{ $t('item_code') }}: {{ item.item_code }}</span>
              <span v-if="item.variant" class="mx-1">·</span>
              <span v-if="item.variant">{{ $t('variant') }}: {{ item.variant }}</span>
            </p>
          </div>
          <button
            type="button"
            class="btn-close"
            data-bs-dismiss="modal"
            aria-label="Close"
          ></button>
        </div>

        <div class="modal-body">
          <div v-if="loading" class="text-center py-5">
            <div class="spinner-border text-primary" role="status"></div>
            <p class="mb-0 mt-2 text-muted">{{ $t('loading') }}…</p>
          </div>

          <div v-else-if="error" class="text-center text-danger py-5">
            <i class="ti ti-alert-circle fs-3"></i>
            <p class="mb-0 mt-2">{{ error }}</p>
          </div>

          <div v-else-if="!batches.length" class="text-center py-5">
            <i class="ti ti-packages fs-1 text-muted"></i>
            <h5 class="text-muted mt-3">{{ $t('batch_details_empty') }}</h5>
            <p class="text-secondary small mb-0">{{ $t('batch_details_empty_hint') }}</p>
          </div>

          <div v-else class="table-responsive">
            <table class="table table-bordered table-hover mb-0">
              <thead class="thead-light">
                <tr>
                  <th>{{ $t('voucher_no') }}</th>
                  <th>{{ $t('batch_no') }}</th>
                  <th>{{ $t('lot_no') }}</th>
                  <th>{{ $t('supplier_name') }}</th>
                  <th>{{ $t('warehouse_name') }}</th>
                  <th>{{ $t('costing_unit_price') }}</th>
                  <th>{{ $t('quantity') }}</th>
                  <th>{{ $t('current_quantity') }}</th>
                  <th>{{ $t('in_house_date') }}</th>
                  <th>{{ $t('expiry_date') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="batch in batches" :key="batch.id">
                  <td>{{ batch.voucher_no || '—' }}</td>
                  <td>{{ batch.batch_no || '—' }}</td>
                  <td>{{ batch.lot_no || '—' }}</td>
                  <td>{{ batch.supplier_name || '—' }}</td>
                  <td>{{ batch.warehouse_name || '—' }}</td>
                  <td>{{ formatMoney(batch.costing_unit_price) }}</td>
                  <td>{{ formatQty(batch.quantity) }}</td>
                  <td>{{ formatQty(batch.current_qty) }}</td>
                  <td>{{ formatDate(batch.in_house_date) }}</td>
                  <td>{{ formatDate(batch.expiry_date) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import apiUrls from '@/urls';
import { truncateMoney2 } from '@/utils';

export default {
  name: 'ItemBatchDetailsModal',
  props: {
    itemId: {
      type: [Number, String],
      default: null,
    },
    variantId: {
      type: [Number, String],
      default: null,
    },
  },
  data() {
    return {
      loading: false,
      error: '',
      item: {},
      batches: [],
    };
  },
  watch: {
    itemId: {
      immediate: true,
      handler(id) {
        if (id) this.fetchBatches(id, this.variantId);
      },
    },
    variantId(id) {
      if (this.itemId) this.fetchBatches(this.itemId, id);
    },
  },
  methods: {
    formatMoney(amount) {
      return truncateMoney2(amount);
    },
    formatQty(value) {
      const n = Number(value);
      if (!Number.isFinite(n)) return '0';
      if (Number.isInteger(n)) return String(n);
      return String(Math.round(n * 1000) / 1000);
    },
    formatDate(value) {
      if (!value) return '—';
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return value;
      return d.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    },
    async fetchBatches(itemId, variantId = null) {
      this.loading = true;
      this.error = '';
      this.item = {};
      this.batches = [];
      try {
        const url = apiUrls.item.batches.replace(':item_id', itemId);
        const params = {};
        if (variantId != null && variantId !== '' && variantId !== 0 && variantId !== '0') {
          params.variant_id = variantId;
        }
        const { data } = await this.$axios.get(url, { params });
        this.item = data.item || {};
        this.batches = data.batches || [];
      } catch (e) {
        this.error = e?.response?.data?.detail || e?.message || this.$t('batch_details_load_failed');
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.batch-eyebrow {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #64748b;
  margin: 0;
}
</style>
